import { normalizeFavourites } from "./favouriteTeams";

function normalizeAllowPlayerNameEditFlag(value: unknown): boolean {
  if (value === true) return true;
  if (value === 1) return true;
  if (value === "1") return true;
  if (typeof value === "string" && value.trim().toLowerCase() === "true") return true;
  return false;
}

/**
 * Maps API session/account payloads (`profile_pic` / `original_profile_pic`)
 * to frontend `avatar` / `original_avatar`. Responses may use either shape.
 */
export function normalizeSessionPayload<T extends Record<string, unknown>>(
  raw: T
): Omit<T, 'profile_pic' | 'original_profile_pic'> & {
  avatar: string;
  original_avatar: string | null;
  allow_player_name_edit: boolean;
} {
  const profilePic = raw['profile_pic'];
  const avatarUrlField = raw['avatar_url'];
  const avatarRaw = raw['avatar'];
  const nonEmpty = (v: unknown) =>
    typeof v === 'string' && String(v).trim() !== '' ? String(v).trim() : '';
  // Same precedence as the backend (`COALESCE(avatar_url, profile_pic)`): a chosen or admin-uploaded
  // avatar beats the sign-in provider photo.
  const avatar =
    nonEmpty(avatarRaw) ||
    nonEmpty(avatarUrlField) ||
    nonEmpty(profilePic) ||
    '';
  const orig = raw['original_avatar'] ?? raw['original_profile_pic'];
  const original_avatar =
    typeof orig === 'string' && String(orig).trim() !== '' ? orig : null;
  const allow_player_name_edit = normalizeAllowPlayerNameEditFlag(raw['allow_player_name_edit']);

  const { profile_pic: _pp, original_profile_pic: _opp, ...rest } = raw as T & {
    profile_pic?: unknown;
    original_profile_pic?: unknown;
  };
  const normalized = {
    ...(rest as Omit<T, 'profile_pic' | 'original_profile_pic'>),
    avatar,
    original_avatar,
    allow_player_name_edit,
  };
  // Map legacy favourite keys (favipl/favcricketint/favteams/…) to the current shape.
  if ('favourites' in raw && raw['favourites'] && typeof raw['favourites'] === 'object') {
    (normalized as Record<string, unknown>).favourites = normalizeFavourites(raw['favourites']);
  }
  return normalized;
}

/** Match backend `accountSharesOnlinePresence` for JSONB / client state. */
export function allowsOnlinePresenceShare(activity_share: Record<string, unknown> | null | undefined): boolean {
  const a = activity_share && typeof activity_share === "object" ? activity_share : {};
  if (a.online_presence === false) return false;
  if (a.presence === false) return false;
  return true;
}

function coerceOnlineFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "string" && value.trim().toLowerCase() === "true") return true;
  return false;
}

/**
 * Own profile “online” dot: only when you allow others to see presence and the session says you’re
 * recently active (`is_online` from API, refreshed after successful presence heartbeats).
 */
export function showOnlineOnOwnProfile(user: {
  is_online?: boolean;
  activity_share?: Record<string, unknown> | null;
}): boolean {
  if (!allowsOnlinePresenceShare(user.activity_share)) return false;
  return coerceOnlineFlag(user.is_online);
}

/** Display URL for any row/object that may expose `avatar`, `avatar_url`, or legacy `profile_pic`. */
export function resolveAvatarUrl(
  source: {
    avatar?: string | null;
    avatar_url?: string | null;
    profile_pic?: string | null;
  } | null | undefined,
): string | null {
  const u = source?.avatar ?? source?.avatar_url ?? source?.profile_pic;
  return typeof u === 'string' && u.trim() !== '' ? u : null;
}

/** Legacy broken local placeholder — treat as no photo everywhere. */
export const MISSING_AVATAR_PLACEHOLDER_PATH = '/img/public/avatar.png';

/** True when the URL is non-empty and not the legacy missing placeholder path. */
export function isUsableAvatarUrl(url: string | null | undefined): boolean {
  const u = url == null ? '' : String(url).trim();
  return u !== '' && u !== MISSING_AVATAR_PLACEHOLDER_PATH;
}

/** Bot S3 catalogue defaults to 64px thumbs; swap to 128px for the profile modal hero. */
export function toProfileHeroAvatarUrl(url: string | null | undefined): string {
  const u = url == null ? '' : String(url).trim();
  if (!isUsableAvatarUrl(u)) return '';
  if (u.includes('/avatars/thumbs/64/')) {
    return u.replace('/avatars/thumbs/64/', '/avatars/thumbs/128/');
  }
  return u;
}
